import { useCallback, useState } from 'react';
import { View } from 'react-native';

import { ProvideReferenceList } from './ProvideReferenceList';
import { SearchingStatementList } from './SearchingStatementList';
import { CitationWizardSteps, InlineReviewPhase } from './statementTriageWizard';
import { useNotifyWhenReady } from '../../hooks/useNotifyWhenReady';
import type { CitationRequest } from '../../model/citationRequest';
import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementReferenceHelpRequest,
} from '../../model/extractedStatements';
import type { StatementTriageEntryMode } from '../../model/statementTriage';
import type { StatementTriagePhase } from '../../state/statementTriageStore';

import { MobileNumberSetupForm } from '@/components/MobileNumberSetupForm';
import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { LoaderButton } from '@/components/ui/loader-button';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { PhaseEnter } from '@/components/wizard/PhaseEnter';
import { MOTION } from '@/constants/motion';
import { SMS_NOTIFICATION_COPY } from '@/constants/smsPromptCopy';
import { useSmsNotifySetup } from '@/hooks/useSmsNotifySetup';
import { useToaster } from '@/hooks/useToaster';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { trackReferenceWizardNotifyRequested } from '@/util/analyticsEvents';

interface StatementTriagePhasesProps {
  conversationId: string;
  messageSid: string;
  extractedStatements: ExtractedStatement[];
  attachedStatementReferences: readonly AttachedStatementReference[];
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
  /** Wizard phase + review state lifted from `useStatementTriageSheet`
   * by the platform wrapper. The hook runs once in the wrapper (so its
   * open-transition analytics fire once); this component only renders
   * from the lifted values. */
  phase: StatementTriagePhase;
  activeCitationRequest: CitationRequest | undefined;
  isSaving: boolean;
  errorMessage: string | null;
  /** True while the wizard is auto-starting a fresh search — shows a
   * brief "starting search" spinner. The statement picker has been
   * removed: the search fires automatically for all statements. */
  isAutoStartPending: boolean;
  /** Re-fire the auto-start search. Surfaced as a "Try again" button if
   * the initial auto-start fails. */
  onRetry: () => void;
  handleDismiss: () => void;
  /** Selects the wizard's step set and selection-prompt copy. `search`
   * (select → search → review) or `provide` (select → add). Defaults to
   * `search`. */
  entryMode?: StatementTriageEntryMode;
  /** Provide-entry wiring, lifted from `useProvideReferencePublish` by
   * the platform wrapper. Present only on surfaces that host the provide
   * flow (the web sheet today); absent on the native sheet, which never
   * reaches the provide phase. When absent the provide phase renders
   * nothing. */
  provide?: {
    statementIndices: readonly number[];
    isPublishing: boolean;
    errorMessage: string | null;
    canPublish: boolean;
    onPublish: () => void;
  };
}

/**
 * Shared phase body for the reference-request wizard's
 * selection → searching → review steps. Both platform wrappers
 * (`StatementTriageSheet` BottomSheetModal on native,
 * `StatementTriageSheet.web` Modal on web) render this between their
 * overlay chrome and their header, so the phase tree, the save label,
 * and the notify/restart handlers live in one place instead of drifting
 * across the two files. The wrappers own only the overlay, the header
 * title, and the close affordance.
 *
 * Kept in its own module (rather than alongside the other wizard
 * subcomponents in `statementTriageWizard.tsx`) so importing those
 * subcomponents doesn't pull the statement-list dependency tree into a
 * consumer's module graph.
 */
export function StatementTriagePhases({
  conversationId,
  messageSid,
  extractedStatements,
  attachedStatementReferences,
  statementReferenceHelpRequests,
  phase,
  activeCitationRequest,
  isSaving,
  errorMessage,
  isAutoStartPending,
  onRetry,
  handleDismiss,
  entryMode = 'search',
  provide,
}: StatementTriagePhasesProps) {
  const toast = useToaster();

  const persistNotifyOptIn = useNotifyWhenReady();

  const { smsWillReach, hasPhoneNumber, mobilePhoneSliced, referencesReadySmsEnabled } =
    useSmsNotifySetup();

  /** When true, an inline phone-setup card is shown (in place of the
   * searching footer button, or on its own once results land) instead of a
   * body takeover or a stacked modal (modal-over-modal is an a11y/UX
   * anti-pattern, and on native the wizard is a full-screen route an overlay
   * would render behind). The prompt PERSISTS until the expert resolves it
   * (Save or Not now) — `resolvePhoneSetup` then reveals the ready review or
   * dismisses — so the review surface is held back while it's open rather
   * than yanking the prompt out from under them when the search completes. */
  const [showPhoneSetup, setShowPhoneSetup] = useState(false);

  /**
   * Dismiss the wizard and confirm to the expert that they'll be
   * notified when the search completes. Without the toast the dismiss
   * is silent and easily mistaken for a cancel.
   *
   * Sets the backend `notifyWhenReady` flag on the active request so the
   * references-ready notification actually fires when the request
   * reaches `awaiting_review`. The POST is fire-and-forget: the toast
   * confirms optimistically and the dismiss is instant, so the expert is
   * never blocked on the network. Because the promise ("we'll let you
   * know") is user-visible, the write is retried a bounded number of
   * times on failure (the endpoint is idempotent) before the failure is
   * captured to Sentry. On success the response reconciles the cached
   * `notifyWhenReady` so a re-open reflects the opt-in.
   */
  const finishNotify = useCallback(() => {
    handleDismiss();

    toast.showSuccess({
      title: 'On it',
      message: "We'll let you know when your references are ready.",
    });

    if (!activeCitationRequest) {
      return;
    }

    void persistNotifyOptIn(activeCitationRequest.id, conversationId, messageSid);
  }, [activeCitationRequest, conversationId, handleDismiss, messageSid, persistNotifyOptIn, toast]);

  /**
   * Resolve the inline phone-setup step (after Save or Skip). Show
   * whatever the wizard would naturally show next:
   * - If the search finished while they were entering a number, the
   *   references are ready and the expert is still here — reveal the
   *   review by clearing the phone-setup overlay rather than closing on
   *   ready results only to send a "come back" notification.
   * - Otherwise the search is still running and they asked to be
   *   notified — dismiss and confirm; the referencesReady deep link
   *   brings them back to review when it completes.
   */
  const resolvePhoneSetup = useCallback(() => {
    if (phase === 'review' && activeCitationRequest) {
      setShowPhoneSetup(false);

      return;
    }

    finishNotify();
  }, [phase, activeCitationRequest, finishNotify]);

  /**
   * Searching-phase "Notify me when ready". Reveal the inline phone-setup
   * form whenever SMS won't reach the expert — no number, or a number on
   * file with `referencesReady` SMS off — so the prompt can fix either.
   * When SMS will already reach them, just dismiss and confirm.
   */
  const handleNotifyWhenReady = useCallback(() => {
    trackReferenceWizardNotifyRequested({
      conversationId,
      messageSid,
      hasPhoneNumber,
      referencesReadySmsEnabled,
    });

    if (!smsWillReach) {
      setShowPhoneSetup(true);

      return;
    }

    finishNotify();
  }, [
    conversationId,
    messageSid,
    finishNotify,
    hasPhoneNumber,
    referencesReadySmsEnabled,
    smsWillReach,
  ]);

  return (
    <>
      {/* Provide mode opens directly on the `provide` phase and has its
          own header CTA, so it shows no step indicator. Search mode keeps
          the search → review stepper. */}
      {entryMode === 'search' ? <CitationWizardSteps phase={phase} /> : null}
      {/* The statement picker has been removed: opening the search wizard
          fires the search for all statements automatically. A brief spinner
          covers the pre-fire frame and the in-flight create POST; if the
          create fails, the selection phase shows the error with a retry.
          One persistent live-region container holds both states so the
          spinner → error swap is announced (a freshly-mounted live region
          often misses its first paint). */}
      {phase === 'selection' && (isAutoStartPending || errorMessage) ? (
        <VStack
          className="items-center justify-center gap-3 px-4 py-12"
          accessibilityLiveRegion="polite">
          {isAutoStartPending ? (
            <>
              <Spinner
                size="large"
                aria-hidden
                accessibilityElementsHidden
                importantForAccessibility="no-hide-descendants"
              />
              <Text size="sm" className="text-typography-600">
                Starting reference search…
              </Text>
            </>
          ) : (
            <>
              <Text size="sm" className="text-center text-error-600">
                {errorMessage}
              </Text>
              <Button
                onPress={onRetry}
                action="primary"
                variant="solid"
                size="md"
                isDisabled={isSaving}
                hitSlop={getAccessibleHitSlop(40)}
                aria-label="Try the reference search again">
                <ButtonText>Try again</ButtonText>
              </Button>
            </>
          )}
        </VStack>
      ) : null}
      {phase === 'searching' ? (
        <PhaseEnter preset={MOTION.entrance.standard}>
          <SearchingStatementList
            messageSid={messageSid}
            extractedStatements={extractedStatements}
          />
          {showPhoneSetup ? null : (
            <HStack className="w-full items-center justify-end gap-2 px-4 pb-4 pt-2">
              <Button
                onPress={handleNotifyWhenReady}
                action="secondary"
                variant="outline"
                size="sm"
                {...getAccessibilityProps({
                  label: 'Notify me when ready',
                  role: 'button',
                })}>
                <ButtonText>Notify me when ready</ButtonText>
              </Button>
            </HStack>
          )}
        </PhaseEnter>
      ) : null}
      {phase === 'provide' && provide && !showPhoneSetup ? (
        <PhaseEnter preset={MOTION.entrance.standard}>
          <ProvideReferenceList
            messageSid={messageSid}
            extractedStatements={extractedStatements}
            statementIndices={provide.statementIndices}
            isPublishing={provide.isPublishing}
            onSubmit={provide.onPublish}
          />
          {provide.errorMessage ? (
            <Text size="sm" className="px-4 pb-1 text-error-600" accessibilityLiveRegion="polite">
              {provide.errorMessage}
            </Text>
          ) : null}
          <HStack className="w-full items-center justify-end gap-2 px-4 pb-4 pt-2">
            <LoaderButton
              onPress={provide.onPublish}
              action="primary"
              variant="solid"
              size="sm"
              isLoading={provide.isPublishing}
              isDisabled={!provide.canPublish}
              aria-label="Publish references">
              Publish
            </LoaderButton>
          </HStack>
        </PhaseEnter>
      ) : null}
      {/* Review is held back while the phone prompt is open so the prompt
          isn't yanked out when the search completes — `resolvePhoneSetup`
          clears the prompt and reveals review once the expert acts. */}
      {phase === 'review' && activeCitationRequest && !showPhoneSetup ? (
        <PhaseEnter preset={MOTION.entrance.standard} fill>
          <InlineReviewPhase
            conversationId={conversationId}
            citationRequest={activeCitationRequest}
            extractedStatements={extractedStatements}
            attachedStatementReferences={attachedStatementReferences}
            statementReferenceHelpRequests={statementReferenceHelpRequests}
            onPublishSuccess={handleDismiss}
          />
        </PhaseEnter>
      ) : null}
      {/* Phone-setup prompt: rendered on `showPhoneSetup` independent of phase
          so it persists across the searching → review transition until the
          expert resolves it (Save or Not now). */}
      {showPhoneSetup ? (
        <PhaseEnter preset={MOTION.entrance.standard}>
          <VStack
            className="mx-4 mb-4 mt-2 gap-3 rounded-lg border border-outline-100 p-4"
            accessibilityLiveRegion="polite">
            <Text size="md" className="font-semibold text-typography-900">
              {SMS_NOTIFICATION_COPY.reference_wizard.heading}
            </Text>
            <Text size="sm" className="text-typography-700">
              {hasPhoneNumber
                ? "We'll text you the moment your references are ready to review."
                : "Add your mobile number and we'll text you the moment your references are ready to review."}
            </Text>
            <MobileNumberSetupForm
              source="reference_wizard"
              showHeader={false}
              autoFocus
              enableSmsOnSave
              showSuccessToast={false}
              defaultMobilePhone={mobilePhoneSliced}
              requireDirty={!hasPhoneNumber}
              onComplete={resolvePhoneSetup}
              onSkip={resolvePhoneSetup}
            />
          </VStack>
        </PhaseEnter>
      ) : null}
    </>
  );
}
